import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, Loader2, ScanLine, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ABtn, AInput, ALabel } from "@/pages/admin/AdminLayout";

export const BarcodeScanner = ({ onProduct, onClose }) => {
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const scannerRef = useRef(null);

  const lookup = async (code) => {
    if (!code?.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get("/stock/lookup", { params: { code: code.trim() } });
      toast.success(`Produit trouvé : ${data.name}`);
      onProduct(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startCamera = async () => {
    setCamera(true);
    try {
      const scanner = new Html5Qrcode("ds-scanner-view", { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 160 } },
        async (text) => {
          await scanner.stop().catch(() => {});
          scannerRef.current = null;
          setCamera(false);
          lookup(text);
        },
        () => {}
      );
    } catch (e) {
      setCamera(false);
      toast.error("Caméra indisponible : saisissez le code manuellement.");
    }
  };

  useEffect(
    () => () => {
      if (scannerRef.current) scannerRef.current.stop().catch(() => {});
    },
    []
  );

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" data-testid="barcode-scanner-modal">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-white">
            <ScanLine className="h-5 w-5 text-gold" /> Scanner un produit
          </h3>
          <ABtn variant="ghost" onClick={onClose} data-testid="scanner-close"><X className="h-4 w-4" /></ABtn>
        </div>

        <div id="ds-scanner-view" className={`mt-4 overflow-hidden rounded-xl ${camera ? "block" : "hidden"}`} />

        {!camera && (
          <ABtn variant="gold" onClick={startCamera} className="mt-4 w-full" data-testid="scanner-start-camera">
            <Camera className="h-4 w-4" /> Activer la caméra
          </ABtn>
        )}

        <div className="mt-4">
          <ALabel>Ou saisir / doucher le code (code-barres ou SKU)</ALabel>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              lookup(manual);
            }}
            className="mt-1 flex gap-2"
          >
            <AInput value={manual} onChange={(e) => setManual(e.target.value)} placeholder="DS-TEL-001 ou 6941487…" autoFocus data-testid="scanner-manual-input" />
            <ABtn onClick={() => lookup(manual)} disabled={busy} data-testid="scanner-manual-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Chercher"}
            </ABtn>
          </form>
          <p className="mt-2 text-[11px] text-slate-500">
            Astuce : associez un code-barres à un produit depuis l'inventaire pour le retrouver instantanément.
          </p>
        </div>
      </div>
    </div>
  );
};
